import React, { useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { SupportQr } from '@/components/organisms/self-checkin/payment/support-qr'
import { ProgressDialog } from '@/components/organisms/self-checkin/payment/progress-dialog'
import QrReader from 'react-qr-reader'
import { SuccessDialog } from '@/components/organisms/self-checkin/payment/success-dialog'
import { ErrorDialog } from '@/components/organisms/self-checkin/payment/error-dialog'
import { useSelfCheckInState } from '@/hooks/use-self-check-in-state'
import * as vescajs from '@/apis/vescajs'
import { useGuestAppUrl } from '@/hooks/use-guest-app-url'
import { SelfCheckinLayout } from '@/components/layouts/self-checkin-layout'
import { reportError } from '@/components/apps/error-boundary'
import { createSelfCheckinResponsiveStyle } from '@/utils/front-tablet-responsive-style'

type TerminalStatus = 'progress' | 'success' | 'error'

export const SelfCheckinPaymentTerminalWithQr: React.FC = () => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const { state, doCheckIn, stateErrorHandling } = useSelfCheckInState()
  const [isStopScan, setIsStopScan] = useState(false)
  const [vesca, setVesca] = useState<vescajs.VescaJs>()
  const [receiveTerminalStatus, setReceiveTerminalStatus] = useState<TerminalStatus>()
  const { mypageUrl, homePageUrl } = useGuestAppUrl({
    hotelId: state.hotelId,
    plugin: state.setting.plugin,
  })

  // note ブラウザバックなどで決済ページを復元させないために、履歴にスタックさせないようにreplaceをもちいる
  const goBack = () => history.replace({ pathname: '/self-checkin/payment' })

  const onSuccess = async (event: vescajs.OutputCompleteEvent) => {
    setReceiveTerminalStatus('success')
    try {
      await doCheckIn({
        state,
        language: i18n.language,
        guestAppUrl: { mypageUrl, homePageUrl },
        selectedPaymentMethod: 'CREDIT_CARD',
        paymentResult: {
          kind: 'credit',
          deviceType: vescajs.DeviceType.PaidQr,
          cardBrand: event.pmsResult.paymentMethod,
          ...event.pmsResult,
        },
      })
      history.replace({ pathname: '/self-checkin/issue-room-key' })
    } catch (e) {
      reportError(e)
      alert(t('Please complete the procedure at the front desk'))
      history.push({ pathname: `/self-checkin`, search: `?hotelId=${state.hotelId}` })
    }
  }

  const onScan = (source: string | null) => {
    if (source === null || isStopScan) {
      return
    }
    setIsStopScan(true)
    vesca!.doQrPay({
      amount: state.confirm!.payment!.usage.totalAmount,
      tax: 0,
      qrCode: source,
    })
    setReceiveTerminalStatus('progress')
  }

  useEffect(() => {
    if (!stateErrorHandling(state, 'selectReservation')) {
      return
    }
    const vescaJs = new vescajs.VescaJs({
      hotelId: state.hotelId,
      handler: {
        onCancel: () => {
          setReceiveTerminalStatus(undefined)
          goBack()
        },
        onError: () => {
          setReceiveTerminalStatus('error')
          setTimeout(() => goBack(), 5000)
        },
        onSuccess,
      },
      paymentTarget: {
        reservationIds: state.selectReservation.selectedReservationIds,
        guestRoomAssignIds: state.selectReservation.selectedReservationAndRooms.flatMap(r => r.guestRoomAssignIds),
      },
    })
    setVesca(vescaJs)

    return () => {
      vescaJs.dispose()
    }
  }, [])

  return (
    <SelfCheckinLayout
      state={state}
      header={{ goToPreviousPage: () => goBack(), title: t('Payment details') }}
      subtitle={{ title: t('Hold the code up to the camera') }}
    >
      <div css={contentStyle}>
        <div className="terminal-and-brand-block">
          <div className="terminal-support-block">
            <div className="terminal-image-block">
              <img src={require('@/static/images/self-checkin-payment/terminal/image_03.png')} />
            </div>
            <SupportQr className="brand-image-block" />
          </div>
          <div className="camera-block">
            {/* docs: https://www.npmjs.com/package/react-qr-reader/v/2.2.1 */}
            <QrReader
              showViewFinder={false}
              onScan={onScan}
              onError={() => {}}
              facingMode="user"
              constraints={{
                facingMode: 'user',
                // 極力高解像度で撮影できるように
                width: { ideal: 1920 },
                height: { ideal: 1080 },
              }}
              resolution={1000}
              // スキャンの試行間隔を短く
              delay={200}
            />
            <div className="camera-overlay">
              <div className="operation-message">{t('Please align QR with the frame')}</div>
              <canvas className="image-frame" />
            </div>
          </div>
        </div>
      </div>
      {receiveTerminalStatus === 'progress' && <ProgressDialog />}
      {receiveTerminalStatus === 'success' && <SuccessDialog />}
      {receiveTerminalStatus === 'error' && <ErrorDialog />}
    </SelfCheckinLayout>
  )
}

const contentStyle = css(
  {
    '*': { fontFamily: 'Noto Sans JP', color: '#272727' },
    '.terminal-and-brand-block': {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 465fr) minmax(0, 484fr)',
      gap: 33,
      height: 658,
      backgroundColor: '#ffffff',
      borderRadius: 10,
      padding: 40,
      '.terminal-image-block': {
        img: { display: 'block', width: '100%', height: 278, objectFit: 'cover' },
      },
      '.brand-image-block': {
        width: '100%',
        height: 232,
        marginTop: 24,
        display: 'grid',
        gridTemplateRows: '39px 91px 15px 85px',
        '.payment-title': {
          gridRow: 1,
          boxSizing: 'border-box',
          width: '100%',
          padding: '8px 0 0',
          fontSize: 12,
          lineHeight: '18px',
          letterSpacing: 0.6,
        },
        '> img': {
          gridRow: 2,
          boxSizing: 'border-box',
          width: 'calc(100% - 16px)',
          height: '100%',
          margin: '0 8px',
          padding: 0,
          objectFit: 'contain',
        },
        '.qr-sub-image-block': {
          gridRow: 4,
          display: 'grid',
          gridTemplateColumns: '42.5% 57.5%',
          height: '100%',
          'img:first-of-type': {
            boxSizing: 'border-box',
            width: '100%',
            height: '100%',
            padding: 8,
            borderRight: '1px solid #CCCCCC',
            objectFit: 'contain',
          },
          'img:last-of-type': {
            boxSizing: 'border-box',
            width: '100%',
            height: '100%',
            padding: 8,
            objectFit: 'contain',
          },
        },
      },
      '.camera-block': {
        width: '100%',
        height: 578,
        background: '#000',
        position: 'relative',
        // QrReader
        section: { height: '100%' },
        '.camera-overlay': {
          position: 'absolute',
          inset: 0,
          width: '100%',
          textAlign: 'center',
          '.operation-message': {
            position: 'absolute',
            top: 75,
            width: '100%',
            fontSize: 16,
            lineHeight: '28px',
            fontWeight: 500,
            color: '#ffffff',
            letterSpacing: 0.8,
          },
          '.image-frame': {
            position: 'absolute',
            left: 72,
            top: 142,
            width: 334,
            height: 334,
            borderRadius: 0,
            border: '2px solid #fff',
          },
        },
      },
    },
  },
  createSelfCheckinResponsiveStyle({
    portrait: {
      '.terminal-and-brand-block': {
        borderRadius: 0,
        gridTemplateColumns: '359px 358px',
        gap: 22,
        height: 507,
        '.terminal-image-block img': { height: 214 },
        '.brand-image-block': {
          height: 208,
          marginTop: 19,
          gridTemplateRows: '36px 70px 15px 85px',
          '> img': { width: 'calc(100% - 12px)', margin: '0 6px' },
          '.qr-sub-image-block': {
            height: '100%',
          },
        },
        '.camera-block': {
          height: 427,
          '.camera-overlay': {
            '.operation-message': { top: 54, lineHeight: '22px' },
            '.image-frame': { left: 53, top: 105, width: 247, height: 247 },
          },
        },
      },
    },
    landscape13: {
      '.terminal-and-brand-block': {
        position: 'relative',
        // Figmaの1366px幅ではコンテンツ基準位置から9px左。より広い画面では中央へ戻す。
        left: 'clamp(-9px, calc(100vw - 1375px), 0px)',
        width: 1230,
        gridTemplateColumns: 'minmax(0, 551fr) minmax(0, 545fr)',
        gap: 34,
        height: 733,
        marginTop: 32,
        padding: '40px 50px',
        '.terminal-image-block img': { height: 329 },
        '.brand-image-block': {
          height: 274,
          marginTop: 27,
          gridTemplateRows: '45px 108px 19px 100px',
          '.payment-title': { paddingTop: 9, fontSize: 16, lineHeight: '22px', letterSpacing: 0.8 },
          '.qr-sub-image-block': {
            height: '100%',
            'img:first-of-type, img:last-of-type': { padding: 10 },
          },
        },
        '.camera-block': {
          height: 653,
          '.camera-overlay': {
            '.operation-message': { top: 85, fontSize: 20, lineHeight: '27px', letterSpacing: 1 },
            '.image-frame': { left: 79, top: 160, width: 377, height: 377 },
          },
        },
      },
    },
  }),
)
